import { json, jsonParseLinter } from '@codemirror/lang-json';
import { linter, lintGutter } from '@codemirror/lint';
import { jsonSchema as jsonSchemaExtension } from 'codemirror-json-schema';
import { JSONSchema7 } from 'json-schema';
import { useEffect, useMemo, useState } from 'react';
import { CodeEditor, CodeEditorProps } from '../code-editor';

type JSONObjectEditorProps<T> = {
  value: T;
  onChange?: (next: T) => void;
  jsonSchema?: JSONSchema7;
} & Omit<CodeEditorProps, 'value' | 'onChange'>;

/**
 * A code editor for JSON objects (T) that will fire onChange events when the code editor is blurred if the JSON string
 * in the editor can be successfully parsed.
 */
export function JSONObjectEditor<T>(props: JSONObjectEditorProps<T>) {
  const { jsonSchema, ...rest } = props;

  const [value, setValue] = useState(() => JSON.stringify(props.value, null, 2));

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setValue(JSON.stringify(props.value, null, 2));
  }, [props.value]);

  const extensions = useMemo(() => {
    const jsonExtensions = jsonSchema ? [jsonSchemaExtension(jsonSchema)] : [json(), linter(jsonParseLinter())];
    return [lintGutter(), ...jsonExtensions];
  }, [jsonSchema]);

  return (
    <CodeEditor
      {...rest}
      basicSetup={{
        lineNumbers: true,
      }}
      extensions={extensions}
      value={value}
      onChange={(newValue) => {
        setValue(newValue);
      }}
      onBlur={() => {
        try {
          const json = JSON.parse(value ?? '{}');
          if (props.onChange !== undefined) {
            props.onChange(json);
          }
        } catch (_e) {
          // ignore this error
        }
      }}
    />
  );
}
